/**
 * The theme of a chapter's game (docs/PATH-V2.md §4.1 p.4 «Партия на тему главы», §12.4 «семья тем «Подарки»», §13.1
 * «партия (тема «Подарки»)»; review round 2): the game step of a chapter names its idea (`PathGameSetup.themeFamily`),
 * and «Учитель» opens the game with THAT idea — not with the family of the opening card the strategist chose («центр»,
 * «f7», «крепость»). The badge «Тема: …» on the game screen says it in a word or two. One line at the start of the
 * game (core `coach/lesson/pathTheme.ts`, a 'pathLesson' event with `say: [{ pool, n }]`); the opening card still
 * shapes the arrows, it is just not announced in a game of the path.
 *
 * Glossary §3.2: «достаёт», «охраняет», «подарок / даром»; «размен» is chapter 1.4's own word and this pool is not
 * one of its pools — said without it. No squares, ≤ 2 short sentences. A wording is ADDED, never edited once recorded.
 */
import type { LessonWording } from '../teaching/types.ts';

/** The themes of the chapters' games (stage 1): 1.1 … 1.6. */
export const GAME_THEME_FAMILIES = ['reach', 'gifts', 'save', 'trade', 'check', 'routine'] as const;
export type GameThemeFamily = (typeof GAME_THEME_FAMILIES)[number];

export function isGameThemeFamily(family: string | undefined): family is GameThemeFamily {
  return family !== undefined && (GAME_THEME_FAMILIES as readonly string[]).includes(family);
}

/** `v3.path.game.<family>` — the start line of a chapter's game. */
export const gameThemePool = (family: GameThemeFamily): string => `v3.path.game.${family}`;

/** The text after «Тема: » (a label, never spoken). */
export const GAME_THEME_BADGE_RU: Readonly<Record<GameThemeFamily, string>> = {
  reach: 'Кто достаёт',
  gifts: 'Подарки',
  save: 'Спаси фигуру',
  trade: 'Размен',
  check: 'Шах',
  routine: 'Два вопроса',
};

export const PATH_GAME_THEME_LINES: Record<string, LessonWording[]> = {
  [gameThemePool('reach')]: [{ t: 'Сегодня в партии смотрим, кого достают фигуры — наши и его.' }, { t: 'Играем и смотрим: кого достаёт его фигура? А наша?' }],
  [gameThemePool('gifts')]: [{ t: 'Сегодня в партии ищем подарки! Что можно забрать даром?' }, { t: 'Играем и ищем подарки. Перед ходом посмотри, что стоит без охраны.' }],
  [gameThemePool('save')]: [{ t: 'Сегодня в партии спасаем своих. Достают нашу фигуру — уведи или поставь охрану!' }, { t: 'Играем и бережём фигуры. Кого из наших достают?' }],
  [gameThemePool('trade')]: [{ t: 'Сегодня в партии меняемся честно: отдаём не больше, чем забираем.' }, { t: 'Играем и считаем: кого забираем, кого отдаём?' }],
  [gameThemePool('check')]: [{ t: 'Сегодня в партии — шахи! Даём шах, но фигуру не отдаём.' }, { t: 'Играем с шахами. Шах нам — не страшно: уйдём, закроемся или заберём!' }],
  [gameThemePool('routine')]: [{ t: 'Перед каждым ходом спроси себя: что он хочет? И безопасен ли мой ход?' }, { t: 'Играем с двумя вопросами: что он хочет? И не отдаю ли я фигуру?' }],
};
